import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  InputAdornment,
  Box,
  SxProps,
  Theme,
  Popper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
} from "@mui/material";
import searchIcon from "../../assets/svgs/search-icon.svg";
import { SearchFieldStyles } from "./SearchFieldStyles";
import { SEARCHFIELD_PLACEHOLDER, SEARCH_ICON } from "../../constants";
import styled from "@emotion/styled";
import AgentSearchPopper from "../../Agents/AgentSearchPopper/AgentSearchPopper";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { AgentGlobalSearch } from "../../Agents/AgentSearchPopper/AgentSearchAction";
import { getLookupListByName } from "../../admin/Lookup/lookupAction";
import { RootState } from "../../store/store";
import { getCookie } from "../../utils/Utility";

interface SearchFieldProps {
  additionalClassName?: SxProps<Theme>;
  handleSearch?: () => void;
  placeholder?: string;
}
const StyledSearchField = styled(TextField)({
  width: "536px",
  padding: "10px",
  borderRadius: "5px",
  fontSize: "16px",
  outline: "none",
  position: "relative",
  "& .MuiOutlinedInput-root": {
    height: "48px",
    "&.Mui-focused fieldset": {
      borderColor: "black",
    },
    "& .MuiInputBase-input": {
      width: "88%",
      height: "32%",
    },
  },
});
const SearchField: React.FC<SearchFieldProps> = ({
  additionalClassName,
  handleSearch,
  placeholder = SEARCHFIELD_PLACEHOLDER,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const system = getCookie("system");
  const carrier = getCookie("carrier");
  const searchFieldRef = useRef<HTMLDivElement | null>(null);
  const { isSuccess, calledReducerType } = useAppSelector(
    (state) => state.common
  );
  const { lookupListByName } = useAppSelector(
    (store: RootState) => store.lookup
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      selectedOption === "Mobile Number" &&
      searchTerm.length > 0 &&
      searchTerm.length < 10
    ) {
      setErrorMessage("Mobile number must be at least 10 digits.");
      return;
    }
    setErrorMessage("");
    if (searchFieldRef.current && searchTerm?.length > 0) {
      setAnchorEl(searchFieldRef.current);

      const requestBody = {
        searchText: searchTerm,
        carrier: carrier,
        system: system,
        searchType: selectedOption,
      };
      dispatch(AgentGlobalSearch(requestBody));
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "search-field-popper" : undefined;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<string>("Customer Name");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (selectedOption === "Mobile Number") {
      if (!/^\d*$/.test(value)) {
        setErrorMessage("Invalid input. Use digits only.");
        return;
      } else {
        setErrorMessage("");
      }

      if (value.length > 10) {
        setErrorMessage("Mobile number should be 10 digits.");
        return;
      }

      if (value.length === 10 || value.length === 0) {
        setErrorMessage("");
      }
    } else {
      setErrorMessage("");
    }

    setSearchTerm(value);
  };

  const handleSelectChange = (event: any) => {
    setSelectedOption(event.target.value);
    setSearchTerm("");
    setErrorMessage("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleClick(event as any);
    }
  };

  useEffect(() => {
    dispatch(getLookupListByName("agentSearch"));
  }, [dispatch]);

  return (
    <Box 
      id="search-field-container"
      data-test-id="test-search-field-container"
      aria-label="Search field container"
    >
      <Box 
        id="search-field-wrapper"
        data-test-id="test-search-field-wrapper"
        aria-label="Search field wrapper"
        sx={SearchFieldStyles.searchContainer}
      >
        <Box 
          id="search-field-select-container"
          data-test-id="test-search-field-select-container"
          aria-label="Search field select container"
        >
          <FormControl>
            <Select
              id="search-field-select"
              data-test-id="test-search-field-select"
              aria-label="Search field select dropdown"
              value={selectedOption}
              onChange={handleSelectChange}
              variant="outlined"
              style={{ height: "48px" }}
              displayEmpty
            >
              {lookupListByName &&
                lookupListByName?.map((item: any) => {
                  return (
                    <MenuItem 
                      key={item.code} 
                      value={item.label}
                      id={`search-field-option-${item.code}`}
                      data-test-id={`test-search-field-option-${item.code}`}
                      aria-label={`Search by ${item.label}`}
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </Box>
        <Box 
          id="search-field-input-container"
          data-test-id="test-search-field-input-container"
          aria-label="Search field input container"
          sx={SearchFieldStyles.searchContainerTwo}
        >
          <Box 
            id="search-field-input-wrapper"
            data-test-id="test-search-field-input-wrapper"
            aria-label="Search field input wrapper"
            ref={searchFieldRef}
          >
            <StyledSearchField
              id="search-field-input"
              data-test-id="test-search-field-input"
              aria-label="Search input field"
              value={searchTerm}
              sx={{ ...additionalClassName }}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment 
                    position="start" 
                    sx={{ cursor: "pointer" }}
                    id="search-field-adornment"
                    data-test-id="test-search-field-adornment"
                    aria-label="Search field input adornment"
                  >
                    <Box
                      id="search-field-icon-container"
                      data-test-id="test-search-field-icon-container"
                      aria-label="Search icon container"
                      sx={SearchFieldStyles.searchIcon}
                      onClick={handleClick}
                    >
                      <img 
                        id="search-field-icon"
                        data-test-id="test-search-field-icon"
                        aria-label="Search icon"
                        src={searchIcon} 
                        alt={SEARCH_ICON} 
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            {errorMessage && (
              <Typography 
                id="search-field-error"
                data-test-id="test-search-field-error"
                aria-label="Search field error message"
                sx={SearchFieldStyles.errorMsg}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>
          <Box 
            id="search-field-popper-container"
            data-test-id="test-search-field-popper-container"
            aria-label="Search results popper container"
          >
            {isSuccess && calledReducerType === "search/agentGlobalSearch" && (
              <AgentSearchPopper
                id={id}
                open={open}
                anchorEl={anchorEl}
                handleClose={handleClose}
                selectedOption={selectedOption}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SearchField;
